/**
 * stickyElement is an object that can be used by all the app,
 * it allows an element to be sticky according to breakpoints.
 */

/* https://developers.google.com/web/updates/2016/06/passive-event-listeners */
// Test via a getter in the options object to see if the passive property is accessed
// Source : https://github.com/WICG/EventListenerOptions/blob/gh-pages/explainer.md
let supportsPassive = false;
try {
  const opts = Object.defineProperty({}, 'passive', {
    get() { return supportsPassive = true; }
  });
  window.addEventListener('testPassive', null, opts);
  window.removeEventListener('testPassive', null, opts);
} catch (e) {} // eslint-disable-line no-empty

export const stickyElement = {
  /**
   * stickyElement is an object that can be used by all the app,
   * it allows an element to be sticky according to breakpoints.
   *
   * @param {Object} data
   * @param {boolean} data.init
   * @param {string} data.selector
   * @param {function?} data.stickyCondition
   * @param {number|function} data.fixedStart - when the sticky starts always on the same position, whatever happend
   * @param {number} data.fixedEnd - when the sticky ends always on the same position, whatever happend
   * @param {string} data.fixedStartSelector - css selector to bind fixedStart value in data attribute, not mandatory
   * @param {string} data.nameSpace - to create a specific event name, use an unique string each time
   * @param {string} data.classSticky - function to execute
   * @param {string} data.deviceList - to trigger sticky element only on specific devices
   * @param {string} data.callBack - dispatcher action string ('lorem:ipsum:setSticky')
   * @param {string} data.callBackUnset - dispatcher action string ('lorem:ipsum:unsetSticky')
   * @param {string} data.startStickySelector - css selector to find the start position (if start position varies)
   * @param {string} data.endStickySelector - css selector to find the end position (if start position varies)
   * @param {number} data.gap
   * @namespace StickyElement
   *
   * @example
   * stickyElement.init({
   *   idElement: 'navSticky',
   *   fixedStart: 200,
   *   fixedStartSelector: '.js-element',
   *   fixedEnd: 1200,
   *   startStickySelector: '.js-bookingButton',
   *   endStickySelector: '#js-footer',
   *   classSticky: 'fixedNav',
   *   deviceList: 'desktop,tablet',
   *   nameSpace: 'exampleString'
   * })
   */
  init(data) {
    const $selector = $(data.selector);
    const $window = typeof data.window !== 'undefined' ? $(data.window) : $(window);
    const stickyBase = typeof data.window !== 'undefined' ? document.querySelector(data.window) : window;
    const stickyCondition = typeof data.stickyCondition !== 'undefined' ? data.stickyCondition : () => {
      return true;
    };

    if ($selector.length) {
      const $fixedStartSelector = typeof data.fixedStartSelector !== 'undefined' && typeof data.fixedStart !== 'undefined' ? $(data.fixedStartSelector) : $();

      if (!$selector.hasClass('stickyElement--init--' + data.nameSpace)) {
        $selector.addClass('stickyElement--init--' + data.nameSpace).removeClass(data.classSticky);
        const deviceList = typeof data.deviceList !== 'undefined' ? data.deviceList : 'all';
        const callBack = typeof data.callBack !== 'undefined' ? data.callBack : '';
        const callBackUnset = typeof data.callBackUnset !== 'undefined' ? data.callBackUnset : '';

        const scrolled = () => {
          const window_top = $window.scrollTop();
          const $startStickySelector = $(data.startStickySelector);
          const $endStickySelector = $(data.endStickySelector);
          const endSticky = typeof data.fixedEnd !== 'undefined' ? data.fixedEnd : ($endStickySelector.length ? $endStickySelector.offset().top : 0);

          if (stickyCondition() && window_top >= (typeof data.fixedStart !== 'undefined' ? $fixedStartSelector.length ? parseInt($fixedStartSelector.attr('data-fixedStart')) : data.fixedStart : ($startStickySelector.length ? $startStickySelector.offset().top : 0) + (typeof data.gap !== 'undefined' ? data.gap : 0)) && !(endSticky > 0 && window_top > endSticky)) {
            $selector.addClass(data.classSticky);

            if (callBack !== '') {
              dispatcher.trigger(callBack);
            }
          } else {
            $selector.removeClass(data.classSticky);

            if (callBackUnset !== '') {
              dispatcher.trigger(callBackUnset);
            }
          }
        };

        if ($fixedStartSelector.length) {
          $fixedStartSelector.attr('data-fixedStart', typeof data.fixedStart === 'function' ? data.fixedStart() : data.fixedStart);
        }

        if (this.checkDevice(deviceList)) {
          this.initEventListenerAndRunScrolled(scrolled, stickyBase);
        }

        let windowResizeTimeout = false;

        const resizeCallback = () => {
          if (windowResizeTimeout) {
            clearTimeout(windowResizeTimeout);
            windowResizeTimeout = false;
          }

          windowResizeTimeout = setTimeout(() => {
            stickyBase.removeEventListener(
              'touchmove',
              scrolled,
              supportsPassive ? {passive: true} : false
            );

            stickyBase.removeEventListener(
              'scroll',
              scrolled,
              supportsPassive ? {passive: true} : false
            );

            if (this.checkDevice(deviceList)) {
              if ($fixedStartSelector.length && typeof data.fixedStart === 'function') {
                $fixedStartSelector.attr('data-fixedStart', data.fixedStart());
              }

              this.initEventListenerAndRunScrolled(scrolled, stickyBase);
            } else {
              $selector.removeClass(data.classSticky);
            }
          }, 75);
        };

        $window.on('resize', resizeCallback).on('refreshStickyElement.' + data.nameSpace, () => {
          if (this.checkDevice(deviceList)) {
            scrolled();
          }
        });

        // add this function to list of function to re-init when document size change
        if ((typeof data.init === 'undefined' || !data.init) && !$fixedStartSelector.length) {
          data['init'] = true;
          resize.addToDocumentList(stickyElement, data);
        }
      } else if ($fixedStartSelector.length) {
        $fixedStartSelector.attr('data-fixedStart', typeof data.fixedStart === 'function' ? data.fixedStart() : data.fixedStart);
      }
    }
  },
  /**
   * Check Devices
   * @param {string} deviceList - commas separated device list [all,mobile,tablet,desktop]
   * @return {boolean}
   * @memberOf StickyElement
   * @static
   * @example
   * checkDevice('desktop,tablet');
   */
  checkDevice(deviceList) {
    return deviceList === 'all' || (deviceList.indexOf('mobile') > -1 && DEVICE.isMobile) || (deviceList.indexOf('tablet') > -1 && DEVICE.isTablet) || (deviceList.indexOf('desktop') > -1 && DEVICE.isDesktop);
  },
  /**
   * init EventListener and run scrolled function
   * @param {EventListener|function} scrolled
   * @memberOf StickyElement
   * @static
   */
  initEventListenerAndRunScrolled(scrolled, stickyBase) {
    if (DEVICE.navigator.isIos() && (DEVICE.isMobile || DEVICE.isTablet)) {
      stickyBase.addEventListener(
        'touchmove',
        scrolled,
        supportsPassive ? {passive: true} : false
      );
    }

    stickyBase.addEventListener(
      'scroll',
      scrolled,
      supportsPassive ? {passive: true} : false
    );

    scrolled();
  }
};